import * as React from 'react'
import { useMemo } from 'react'

import { Box, useClassNames, useToggle } from '@factorialco/deprecated-design-system'
import {
  ShiftManagementRolesDocument,
  ShiftManagementTreeRolesDocument,
} from 'generated/resources/graphql'
import { Moment } from 'moment'

import { observer } from 'mobx-react'

import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/index.styles.css'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useUnassignedShifts } from 'modules/shift_management/hooks/useUnassignedShifts'
import type { Shift } from 'modules/shift_management/lib/types'
import UnassignedShifts from 'modules/shift_management/store/internal/collections/UnassignedShifts'
import type PlannedBreaks from 'modules/time_planning/store/internal/collections/plannedBreaks'

import { stylesRefs } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/index.styles'
import AddUnassignedShiftButton from './AddUnassignedShiftButton'
import AddUnassignedShiftModal from './AddUnassignedShiftModal'
import UnassignedShiftsList from './UnassignedShiftsList'

const CoverageShiftsCell = ({
  coverageShifts,
  date,
  plannedBreaks,
}: {
  coverageShifts: Shift[]
  date: Moment
  plannedBreaks: PlannedBreaks
}) => {
  const [openAddShiftModal, setOpenAddShiftModal] = React.useState<boolean>(false)

  const onOpenAddShiftModal = () => {
    track(trackingEvents.shiftManagement.addUnassignedShiftStart, {})
    setOpenAddShiftModal(true)
  }

  const onCloseAddShiftModal = () => {
    setOpenAddShiftModal(false)
  }

  const cx = useClassNames(stylesRefs)
  const [isHovering, toggleHovering] = useToggle(false)

  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const hasLegacyJobCatalog = hasFeatureFlag('legacy_job_catalog')

  // Fetch old roles (legacy schema) - only if feature flag is disabled
  const { data } = useQuery(ShiftManagementRolesDocument, {
    skip: !hasLegacyJobCatalog,
  })
  const roles = data?.jobCatalog?.rolesConnection?.nodes.map((r) => r) || []

  const [{ view }] = useSchedule()
  const [unassignedShifts] = useUnassignedShifts()

  const [{ locationIds }] = useFilters()

  const locationFilterValue = locationIds.at(0)
  const filteredUnassignedShifts = !locationFilterValue
    ? unassignedShifts
    : unassignedShifts.filter(
        (unassignedShift) => unassignedShift.locationId === locationFilterValue
      )
  const treeRoleUuids: string[] = useMemo(() => {
    return filteredUnassignedShifts.flatMap(
      (unassignedShift) => unassignedShift.jobCatalogTreeRoleUuids
    )
  }, [filteredUnassignedShifts])

  const { data: treeRolesData, loading: treeRolesLoading } = useQuery(
    ShiftManagementTreeRolesDocument,
    {
      skip: hasLegacyJobCatalog || treeRoleUuids.length === 0,
      variables: {
        uuids: treeRoleUuids,
      },
    }
  )
  const treeRoles = useMemo(
    () =>
      treeRolesLoading ? undefined : treeRolesData?.jobCatalog?.treeNodesConnection?.nodes || [],
    [treeRolesData?.jobCatalog?.treeNodesConnection?.nodes, treeRolesLoading]
  )

  const unassignedShiftsCollection = new UnassignedShifts(filteredUnassignedShifts)
  const shiftsByDate = coverageShifts.filter((shift) => shift.isDate(date))

  const sortedUnassignedShifts = unassignedShiftsCollection.orderedByDateAndCounter(
    date,
    shiftsByDate
  )

  return (
    <>
      <Box
        height="full"
        border={{
          right: {
            width: 's1',
            style: 'solid',
            color: 'grey300',
          },
        }}
        className={cx('header')}
        padding="s4"
      >
        <Box alignItems="center" height="full" gap="s4" justifyContent="spaceBetween">
          <Box alignItems="center" height="full" width="full">
            {!sortedUnassignedShifts.length ? (
              <Box
                width="full"
                height="full"
                onMouseEnter={toggleHovering(true)}
                onMouseLeave={toggleHovering(false)}
              >
                {isHovering && <AddUnassignedShiftButton onClick={onOpenAddShiftModal} />}
              </Box>
            ) : (
              <UnassignedShiftsList
                date={date}
                unassignedShifts={sortedUnassignedShifts}
                shiftsByDate={shiftsByDate}
                plannedBreaks={plannedBreaks}
                roles={roles}
                treeRoles={treeRoles}
              />
            )}
          </Box>
        </Box>
      </Box>

      {openAddShiftModal && (
        <AddUnassignedShiftModal date={date} onClose={onCloseAddShiftModal} roles={roles} />
      )}
    </>
  )
}

export default observer(CoverageShiftsCell)
